import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { ImageIcon, X } from 'lucide-react';

interface DriveFile {
  id: string;
  name: string;
}

interface FileSelectionResult {
  files: DriveFile[];
  accessToken?: string;
}

interface GoogleDrivePickerProps {
  onFilesSelected: (result: FileSelectionResult) => void;
  maxFiles?: number;
  currentFiles?: DriveFile[];
  label?: string;
  autoOpen?: boolean;  // Auto-open picker on mount
  hideButton?: boolean;  // Hide the button (for inline image replacement)
}

declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initTokenClient: (config: any) => any;
        };
      };
      picker: {
        ViewId: {
          DOCS_IMAGES: string;
        };
        Feature: {
          MULTISELECT_ENABLED: string;
        };
        Response: {
          ACTION: string;
          DOCUMENTS: string;
        };
        Action: {
          PICKED: string;
          CANCEL: string;
        };
        PickerBuilder: new () => any;
      };
      load: (api: string, callback: () => void) => void;
    };
    gapi?: {
      load: (api: string, callback: () => void) => void;
    };
  }
}

export default function GoogleDrivePicker({
  onFilesSelected,
  maxFiles = 5,
  currentFiles = [],
  label = 'Select Images',
  autoOpen = false,
  hideButton = false,
}: GoogleDrivePickerProps) {
  const [selectedFiles, setSelectedFiles] = useState<DriveFile[]>(currentFiles);
  const [currentAccessToken, setCurrentAccessToken] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [pickerLoaded, setPickerLoaded] = useState(false);
  
  // Use ref instead of state to track auto-open (prevents re-render loops)
  const autoOpenTriggeredRef = useRef(false);
  
  // Track previous currentFiles to avoid unnecessary updates
  const prevCurrentFilesRef = useRef<string>('');

  // Sync local state with parent's currentFiles prop
  // Use JSON stringify to do deep comparison and avoid infinite loops
  useEffect(() => {
    const currentFilesJson = JSON.stringify(currentFiles.map(f => f.id));
    if (prevCurrentFilesRef.current !== currentFilesJson) {
      prevCurrentFilesRef.current = currentFilesJson;
      setSelectedFiles(currentFiles);
      // Reset loading state when files are cleared externally
      if (currentFiles.length === 0) {
        setIsLoading(false);
      }
    }
  }, [currentFiles]);

  // Load Google Picker API and Google Identity Services
  useEffect(() => {
    let gapiScriptAdded = false;
    let gisScriptAdded = false;
    let gapiScript: HTMLScriptElement | null = null;
    let gisScript: HTMLScriptElement | null = null;

    const checkReady = () => {
      // Both scripts must be loaded: picker API and OAuth/GIS
      if (window.google?.picker && window.google?.accounts?.oauth2) {
        console.log('[GoogleDrivePicker] Both Picker and GIS loaded');
        setPickerLoaded(true);
      }
    };

    // Check if already loaded
    if (window.google?.picker && window.google?.accounts?.oauth2) {
      setPickerLoaded(true);
      return;
    }

    // Load Google API script (for Picker)
    if (!document.querySelector('script[src="https://apis.google.com/js/api.js"]')) {
      gapiScript = document.createElement('script');
      gapiScript.src = 'https://apis.google.com/js/api.js';
      gapiScript.async = true;
      gapiScript.defer = true;
      
      gapiScript.onload = () => {
        console.log('[GoogleDrivePicker] GAPI script loaded');
        if (window.gapi && window.gapi.load) {
          window.gapi.load('picker', () => {
            console.log('[GoogleDrivePicker] Picker loaded via gapi');
            checkReady();
          });
        }
      };
      
      document.head.appendChild(gapiScript);
      gapiScriptAdded = true;
    } else if (window.google?.picker) {
      checkReady();
    }

    // Load Google Identity Services script (for OAuth)
    if (!document.querySelector('script[src="https://accounts.google.com/gsi/client"]')) {
      gisScript = document.createElement('script');
      gisScript.src = 'https://accounts.google.com/gsi/client';
      gisScript.async = true;
      gisScript.defer = true;
      
      gisScript.onload = () => {
        console.log('[GoogleDrivePicker] GIS script loaded');
        checkReady();
      };
      
      document.head.appendChild(gisScript);
      gisScriptAdded = true;
    } else if (window.google?.accounts?.oauth2) {
      checkReady();
    }

    return () => {
      // Cleanup scripts if we added them
      if (gapiScriptAdded && gapiScript && document.head.contains(gapiScript)) {
        document.head.removeChild(gapiScript);
      }
      if (gisScriptAdded && gisScript && document.head.contains(gisScript)) {
        document.head.removeChild(gisScript);
      }
    };
  }, []);

  const openPicker = useCallback(() => {
    console.log('[GoogleDrivePicker] openPicker called');
    console.log('[GoogleDrivePicker] pickerLoaded:', pickerLoaded);
    console.log('[GoogleDrivePicker] window.google?.picker:', !!window.google?.picker);
    console.log('[GoogleDrivePicker] window.google?.accounts?.oauth2:', !!window.google?.accounts?.oauth2);
    
    if (!pickerLoaded) {
      console.error('[GoogleDrivePicker] Picker not loaded yet');
      alert('Google Picker is still loading. Please wait a moment and try again.');
      return;
    }

    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
    console.log('[GoogleDrivePicker] Client ID:', clientId ? `${clientId.substring(0, 20)}...` : 'NOT SET');
    
    if (!clientId) {
      alert('Google Client ID not configured. Please restart the React dev server after adding REACT_APP_GOOGLE_CLIENT_ID to the .env file.');
      return;
    }

    if (!window.google?.accounts?.oauth2) {
      console.error('[GoogleDrivePicker] Google Identity Services not loaded');
      alert('Google Identity Services not loaded. Please refresh the page.');
      return;
    }

    setIsLoading(true);

    // Safety timeout - reset loading state if OAuth takes too long (e.g., user closes popup)
    const loadingTimeout = setTimeout(() => {
      console.log('[GoogleDrivePicker] Loading timeout - resetting state');
      setIsLoading(false);
    }, 60000); // 60 second timeout

    try {
      // Initialize OAuth token client
      // Using 'drive' scope (not 'drive.readonly') to allow sharing files publicly
      // This is required for images to display in Google Sheets
      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: 'https://www.googleapis.com/auth/drive',
        callback: (tokenResponse: any) => {
          clearTimeout(loadingTimeout); // Clear safety timeout
          console.log('[GoogleDrivePicker] OAuth callback received');
          
          if (tokenResponse.error) {
            console.error('[GoogleDrivePicker] OAuth error:', tokenResponse.error, tokenResponse.error_description);
            setIsLoading(false);
            alert(`OAuth error: ${tokenResponse.error_description || tokenResponse.error}`);
            return;
          }

          console.log('[GoogleDrivePicker] Got access token, building picker...');
          
          // Store access token for later use
          const accessToken = tokenResponse.access_token;
          setCurrentAccessToken(accessToken);
          console.log('[GoogleDrivePicker] Stored access token for backend use');

          // Create picker (Note: we don't use setDeveloperKey for OAuth-only access)
          const picker = new window.google!.picker.PickerBuilder()
            .setOAuthToken(accessToken)
            .setCallback((data: any) => {
              console.log('[GoogleDrivePicker] Picker callback:', data);
              setIsLoading(false);
              
              if (data[window.google!.picker.Response.ACTION] === window.google!.picker.Action.PICKED) {
                const files: DriveFile[] = data[window.google!.picker.Response.DOCUMENTS]
                  .slice(0, maxFiles)
                  .map((doc: any) => ({
                    id: doc.id,
                    name: doc.name,
                  }));
                
                console.log('[GoogleDrivePicker] Files selected:', files);
                console.log('[GoogleDrivePicker] Access token available:', !!accessToken);
                setSelectedFiles(files);
                // Pass both files and access token to parent
                onFilesSelected({ files, accessToken });
              } else if (data[window.google!.picker.Response.ACTION] === window.google!.picker.Action.CANCEL) {
                console.log('[GoogleDrivePicker] Picker cancelled');
                setIsLoading(false);
              }
            })
            .addView(window.google!.picker.ViewId.DOCS_IMAGES)
            .setSelectableMimeTypes('image/jpeg,image/png,image/gif,image/webp,image/svg+xml')
            .enableFeature(window.google!.picker.Feature.MULTISELECT_ENABLED)
            .setMaxItems(maxFiles)
            .build();

          picker.setVisible(true);
        },
      });

      console.log('[GoogleDrivePicker] Requesting access token...');
      // Request access token
      tokenClient.requestAccessToken({ prompt: '' });
    } catch (error) {
      console.error('[GoogleDrivePicker] Error:', error);
      setIsLoading(false);
      alert(`Error initializing picker: ${error}`);
    }
  }, [pickerLoaded, maxFiles, onFilesSelected]);

  // Auto-open picker when loaded if autoOpen is true
  // Uses ref to prevent infinite re-render loops
  useEffect(() => {
    if (autoOpen && pickerLoaded && !autoOpenTriggeredRef.current) {
      autoOpenTriggeredRef.current = true;
      openPicker();
    }
  }, [autoOpen, pickerLoaded, openPicker]);

  const removeFile = (fileId: string) => {
    const updated = selectedFiles.filter((f) => f.id !== fileId);
    setSelectedFiles(updated);
    onFilesSelected({ files: updated, accessToken: currentAccessToken });
  };

  const clearAll = () => {
    setSelectedFiles([]);
    setCurrentAccessToken(undefined);
    setIsLoading(false); // Reset loading state when clearing
    onFilesSelected({ files: [], accessToken: undefined });
  };

  // Column labels for image order
  const columnLabels = ['Product', 'Left', 'Right', 'Top', 'Banner'];

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Select Images Button - can be hidden for inline image replacement */}
      {!hideButton && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={openPicker}
          disabled={isLoading || !pickerLoaded}
          className="text-xs shrink-0 h-9"
        >
          <ImageIcon className="h-3 w-3 mr-1" />
          {isLoading ? 'Loading...' : selectedFiles.length > 0 ? `${selectedFiles.length}/${maxFiles}` : label}
        </Button>
      )}

      {/* Selected files displayed horizontally as badges */}
      {selectedFiles.length > 0 && (
        <>
          {selectedFiles.map((file, index) => (
            <div
              key={file.id}
              className="flex items-center gap-1 text-xs bg-blue-50 border border-blue-200 text-blue-700 pl-2 pr-1 py-1 rounded-full shrink-0"
              title={`${columnLabels[index] || index + 1}: ${file.name}`}
            >
              <span className="font-medium">{columnLabels[index] || index + 1}</span>
              <span className="text-blue-400 mx-0.5">•</span>
              <span className="max-w-[80px] truncate">{file.name.split('.')[0]}</span>
              <button
                type="button"
                onClick={() => removeFile(file.id)}
                className="ml-0.5 hover:bg-blue-200 rounded-full p-0.5 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
          
          {/* Clear all button */}
          <button
            type="button"
            onClick={clearAll}
            className="text-xs text-muted-foreground hover:text-destructive transition-colors shrink-0"
          >
            Clear
          </button>
        </>
      )}
    </div>
  );
}

